import { Col, Row, Card, Nav, NavItem, NavLink } from "reactstrap";
import React, { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import classnames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";

import AppModal from "../../Common/AppModal";
// Task 24584
import LeaveWhoElseIsOff from "./LeaveWhoElseIsOff";
import LeaveRequestHistory from "./LeaveRequestHistory";
// Task 24594
import LeaveRequestNotes from "./LeaveRequestNotes";

import {
  createSingleBankTransaction,
  createSingleBankTransactionSuccess,
  createStaffLeave,
  createStaffLeaveAllowanceSuccess,
  createStaffLeaveRequest,
  createStaffLeaveRequestSuccess,
  getAllBankTransactionList,
  getBankList,
  getItemAllocationList,
  getStaffData,
  getStaffLeaveList,
  getStaffLeaveTypeList,
  getStaffList,
  getUnAllocatedBankTransactionList,
  searchStaffLeave,
  sendLeaveRequest,
  sendLeaveRequestSuccess,
  staffToStaffHolidayCalendarPeriodListCurrentMy,
  staffToStaffHolidayCalendarPeriodListCurrentMyTeamMembers,
  staffLeaveRestrictionPeriodList,
  staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembers,
  updateStaffLeave,
  updateStaffLeaveSuccess,
} from "../../../store/actions";
import Select from "../../Common/AppSelect";
import moment from "moment";
import {
  AppDatePicker,
  AppDateTimePicker,
} from "../../Common/AppDateTimePicker";
import {
  HandleDate,
  handleDateFormat,
  handleDateFormatInRevere,
  timeFormatDateAndTimeNoSeconds,
} from "../../Common/HandleDateTime";
import Bank from "../../../store/bank/reducer";
import LoadingSpinner from "../../Common/Spinner";
import Z_INDEX from "../../Common/zIndexScale";

// Task 24488. The backend refuses a hard-stopped request, but finding that
// out only after pressing Save is a poor way to learn it. The same restriction
// list is checked here as the dates are chosen, so the answer arrives while
// there is still something useful to do about it.
const overlapsRange = (restriction, start, end) => {
  if (!start || !end) return false;

  const toDay = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const rStart = toDay(restriction?.StartDateTime);
  const rEnd = toDay(restriction?.EndDateTime);
  const sStart = toDay(start);
  const sEnd = toDay(end);

  if (rStart === null || rEnd === null || sStart === null || sEnd === null) {
    return false;
  }

  // Compared on dates, not times - a restriction running "24 Dec 09:00 to
  // 26 Dec 17:00" is meant to cover those days, and a full-day request starting
  // at 00:00 should not slip past it.
  return rStart <= sEnd && rEnd >= sStart;
};

const isHardRestriction = (restriction) =>
  String(restriction?.RestrictionType || "HARD").toUpperCase() !== "SOFT";

// "12.00" and "90.00" are precise about nothing - the trailing zeros just
// make the number harder to read at a glance. Half days matter, so 0.5 is kept.
// Task 24583. "Nothing was entered" for an id or a date. Deliberately not
// lodash's isEmpty, which reports every number as empty.
const isBlankValue = (value) =>
  value === null || value === undefined || String(value).trim() === "";

// Task 24583. Midnight of a date, as a UTC timestamp, for day arithmetic.
// "YYYY-MM-DD" is read field by field rather than handed to `new Date`, which
// would parse it as UTC midnight and then shift the day for anyone west of
// Greenwich. Working in whole days also means a 23- or 25-hour DST day cannot
// round the answer off by one.
const startOfDayUtc = (value) => {
  if (!value) return null;
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) return Date.UTC(+parts[1], +parts[2] - 1, +parts[3]);
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return Date.UTC(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );
};

// Days covered by a range, counted inclusively: the 15th to the 18th is four
// days off, not three. Returns null when the range isn't usable yet.
const inclusiveDayCount = (start, end) => {
  const from = startOfDayUtc(start);
  const to = startOfDayUtc(end);
  if (from === null || to === null || to < from) return null;
  return Math.round((to - from) / 86400000) + 1;
};

const trimZeros = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value ?? "0";
  return String(Number(n.toFixed(2)));
};

export default function LeaveRequest({
  modalLeaveRequest,
  setModalLeaveRequest,
  toggleLeaveRequest,
  // Every one of the four call sites (MyShortCuts, Calendar, HolidayRecords,
  // LeaveRequests) passes the close handler as `togglemodalLeaveRequest`, not
  // `toggleLeaveRequest`. Nothing ever passed the name this component read, so
  // `onClose` was undefined — and AppModal falls back to `() => {}`, which made
  // X, Cancel and backdrop-click all silently dead. Accept both spellings.
  togglemodalLeaveRequest,
  page,
  staff_Id,
  isEdit,
  dataUpdate,
  // Task 24582. The table row that was clicked, when editing. `dataUpdate`
  // comes from redux and is whatever was fetched last, so on its own it cannot
  // say which request this modal is actually for.
  editingRow,
  zIndex,
}) {
  const dispatch = useDispatch();

  // `zIndex` exists for one real case: FieldServiceDispatch renders this modal
  // from inside its own MUI Dialog, so it has to clear that parent's 1300.
  // It used to be taken raw from the caller, which is how it ended up at 1400 —
  // the snackbar tier, where this modal covers every toast fired while it is
  // open. Anything above the snackbar is clamped back to the nested-dialog step;
  // a non-numeric value is ignored so AppModal keeps its own default.
  // Last resort: every caller passes setModalLeaveRequest, so closing always works.
  const handleCloseModal =
    toggleLeaveRequest ??
    togglemodalLeaveRequest ??
    (() => setModalLeaveRequest(false));

  const resolvedZIndex = (() => {
    if (zIndex === undefined || zIndex === null) return undefined;
    const n = Number(zIndex);
    if (!Number.isFinite(n)) return undefined;
    return n >= Z_INDEX.snackbar ? Z_INDEX.dialogNested : n;
  })();
  // get from local storage
  console.log(
    ' JSON.parse(localStorage.getItem("authUser"))',
    JSON.parse(localStorage.getItem("authUser"))
  );
  const lStaffUserId = JSON.parse(localStorage.getItem("authUser"))
    ?.get_staff_data?.Staff_Id;
  const lStaffUserFullName = JSON.parse(localStorage.getItem("authUser"))
    ?.get_staff_data?.FullName;
  console.log("lStaffUserFullName", lStaffUserFullName);
  const myTeamMembersList = JSON.parse(
    localStorage.getItem("authUser")
  )?.get_staffteammembers_list;
  console.log("myTeamMembersList", myTeamMembersList);
  // Task 24582. Editing someone else's request used to open with MY name in
  // the title: displayName was seeded from the logged-in user and only
  // corrected once /staffleave/data came back. Any delay - or a request that
  // fails, 403s, or returns a shape without Staff_FullName - left the modal
  // confidently naming the wrong person, which is worse than naming nobody.
  // In edit mode it now starts from the row that was clicked and never falls
  // back to the logged-in user.
  const [displayName, setDisplayName] = useState(
    isEdit ? editingRow?.Staff_FullName ?? "" : lStaffUserFullName ?? ""
  );
  const defaultLeaveRequest = {
    StaffLeaveType_Id: "",
    // Task 24582. Same reasoning for "Whose leave is this?": when editing, the
    // request belongs to whoever it belongs to, not to the page or to me.
    Staff_Id:
      isEdit && editingRow?.Staff_Id
        ? editingRow.Staff_Id
        : staff_Id
        ? staff_Id
        : lStaffUserId,
    StartDateTime: "",
    EndDateTime: "",
    FlagAllDay: "1",
    IncludeTimeRange: "0",
    RequestedDays: "",
    RequestedHours: "",
    RequestedMinutes: "",
    FlagRequestedInputType: "",
    Notes: "",
    FlagStaffEmailNotification: "1",
    MySelf: "1",
  };
  const [leaveRequestData, setLeaveRequestData] = useState(defaultLeaveRequest);


  useEffect(() => {
    if (!dataUpdate) return;

    // Task 24582. Redux holds whichever record was fetched last, so reopening
    // this modal for a different request shows the previous one until the new
    // fetch lands. When we know which row was clicked, ignore anything that
    // isn't it.
    if (
      editingRow?.Id &&
      String(dataUpdate?.Id ?? "") !== String(editingRow.Id)
    ) {
      return;
    }

    // Task 24601. IncludeTimeRange is a UI-only field - it drives the Full
    // Day/s | Partial Day/s toggle and everything below it, but it is not a
    // column, so nothing comes back from /staffleave/data. Spreading the
    // record straight in left it undefined: neither button looked selected,
    // and since the panel below tests `=== "0"`, every saved request - full
    // day ones included - opened showing the partial-day date/time pickers.
    // FlagAllDay is the stored form of the same answer, so derive it from
    // there (the Calendar caller already does exactly this).
    const flagAllDay = String(dataUpdate?.FlagAllDay ?? "1");
    setLeaveRequestData({
      ...dataUpdate,
      FlagAllDay: flagAllDay,
      IncludeTimeRange: flagAllDay === "1" ? "0" : "1",
      RequestedHours: +dataUpdate?.RequestedHours?.toFixed?.(0),
      RequestedMinutes: +(
        ((+dataUpdate?.RequestedHours || 0) -
          Math.floor(+dataUpdate?.RequestedHours || 0)) *
        60
      ).toFixed(0),
    });
    // Fall back to the clicked row rather than to a blank name, so a response
    // missing Staff_FullName doesn't wipe a name we already had.
    setDisplayName(
      dataUpdate?.Staff_FullName ?? editingRow?.Staff_FullName ?? ""
    );
  }, [dataUpdate, editingRow]);

  const {
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI,
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersLoading,
  } = useSelector((state) => ({
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI:
      state.StaffLeave
        .staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembers
        ?.staff_list,
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersLoading:
      state.StaffLeave
        .staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersLoading,
  }));
  console.log(
    "staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI",
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI
  );
  // Derived rather than held separately: a second piece of state saying
  // "this is mine" could disagree with the Staff_Id actually being submitted.
  const isForMyself =
    String(leaveRequestData?.Staff_Id ?? "") === String(lStaffUserId ?? "");

  useEffect(() => {
    dispatch(staffLeaveRestrictionPeriodList());
  }, [dispatch]);

  const { restrictionPeriods } = useSelector((state) => ({
    restrictionPeriods:
      state.StaffLeave?.staffLeaveRestrictionPeriodList
        ?.staffleaverestrictionperiod_list || [],
  }));

  const clashingRestrictions = useMemo(() => {
    return (restrictionPeriods || []).filter((restriction) =>
      overlapsRange(
        restriction,
        leaveRequestData?.StartDateTime,
        leaveRequestData?.EndDateTime
      )
    );
  }, [
    restrictionPeriods,
    leaveRequestData?.StartDateTime,
    leaveRequestData?.EndDateTime,
  ]);

  const hardClash = clashingRestrictions.find(isHardRestriction) || null;
  const softClashes = clashingRestrictions.filter((r) => !isHardRestriction(r));

  const handleSubmitLeaveRequest = () => {
    // A hard stop is refused by the backend anyway; stopping here means the
    // reason is on screen next to the dates that caused it.
    if (hardClash) {
      toast.error(
        `Leave cannot be requested between ${HandleDate(
          hardClash.StartDateTime
        )} and ${HandleDate(hardClash.EndDateTime)}.`
      );
      return;
    }

    // check if all fields are filled
    // Staff
    if (isBlankValue(leaveRequestData.Staff_Id)) {
      toast.error("Choose whose leave this is");
      return;
    }
    // also check type is selected
    // Task 24583 - see handleUpdateLeaveRequest, same two validation faults.
    if (isBlankValue(leaveRequestData.StaffLeaveType_Id)) {
      toast.error("Please Select Leave Type");
      return;
    }
    if (
      isBlankValue(leaveRequestData.StartDateTime) ||
      isBlankValue(leaveRequestData.EndDateTime)
    ) {
      toast.error("Please Start Date and End Date ");
      return;
    }

    const tempData = leaveRequestData;
    tempData.RequestedHours = (
      +tempData.RequestedHours +
      tempData.RequestedMinutes / 60
    )?.toFixed(2);

    dispatch(createStaffLeaveRequest([tempData]));
  };

  const handleUpdateLeaveRequest = () => {
    // check if all fields are filled
    // Staff
    // Task 24583. These used lodash isEmpty, which calls *any* number empty -
    // isEmpty(11) is true. Ids reach us as strings today, so it happened to
    // work, but one call site passing a number would have made Save refuse
    // with "Please Staff" and no way to get past it.
    if (isBlankValue(leaveRequestData.Staff_Id)) {
      toast.error("Please Staff");
      return;
    }
    // also check type is selected
    if (isBlankValue(leaveRequestData.StaffLeaveType_Id)) {
      toast.error("Please Select Leave Type");
      return;
    }
    // Task 24583. `a || b` only ever tested one of the two, so a missing End
    // date sailed through whenever a Start date was set.
    if (
      isBlankValue(leaveRequestData.StartDateTime) ||
      isBlankValue(leaveRequestData.EndDateTime)
    ) {
      toast.error("Please Start Date and End Date ");
      return;
    }

    const tempData = leaveRequestData;
    tempData.RequestedHours = (
      +tempData.RequestedHours +
      tempData.RequestedMinutes / 60
    )?.toFixed(2);

    // Task 24595. The controls are disabled, so this should already hold - but
    // whose request it is decides whose allowance it came off and who the
    // approval chain was built for, so it is pinned back to the record rather
    // than trusted to stay put.
    const originalStaffId = editingRow?.Staff_Id ?? dataUpdate?.Staff_Id;
    if (originalStaffId !== undefined && originalStaffId !== null) {
      tempData.Staff_Id = originalStaffId;
    }

    dispatch(updateStaffLeave([tempData]));
  };

  const { requestSentSuccessResponse } = useSelector((state) => ({
    requestSentSuccessResponse: state.LeaveRequest.requestSentSuccess,
  }));

  const { addStaffLeaveSuccessResponse, staffLeaveCreateRequestLoading } =
    useSelector((state) => ({
      addStaffLeaveSuccessResponse: state.StaffLeave.staffLeaveCreateRequest,
    }));

  // const { staffData } = useSelector((state) => ({
  //   staffData: state.Staff.staffList?.staff_list,
  // }));

  const { staffLeaveTypeListAPI } = useSelector((state) => ({
    staffLeaveTypeListAPI: state.Staff.staffLeaveTypeList?.staffleavetype_list,
  }));

  const [staffLeaveTypeListData, setStaffLeaveTypeListData] = useState([]);

  useEffect(() => {
    if (leaveRequestData?.Staff_Id === lStaffUserId) {
      setStaffLeaveTypeListData(
        staffLeaveTypeListAPI?.filter(
          (item) => item?.FlagShowToStaffRequestor === "1"
        )
      );
    } else {
      setStaffLeaveTypeListData(staffLeaveTypeListAPI);
    }
  }, [leaveRequestData?.Staff_Id, staffLeaveTypeListAPI]);

  useEffect(() => {
    dispatch(getStaffLeaveTypeList());
    dispatch(staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembers());
  }, []);

  useEffect(() => {
    if (isEmpty(requestSentSuccessResponse)) return;
    if (requestSentSuccessResponse?.status === 200) {
      toast.success(requestSentSuccessResponse?.message);
      dispatch(getStaffLeaveList());
      setModalLeaveRequest(false);
    } else {
      toast.error(requestSentSuccessResponse?.message);
    }
    dispatch(sendLeaveRequestSuccess([]));
  }, [requestSentSuccessResponse]);

  useEffect(() => {
    if (isEmpty(addStaffLeaveSuccessResponse)) return;
    if (addStaffLeaveSuccessResponse?.status === 200) {
      toast.success(addStaffLeaveSuccessResponse?.message);
      dispatch(getStaffLeaveList());
      if (page === "StaffSingleLeaveRequestPage") {
        dispatch(
          searchStaffLeave([
            {
              staff_data: {
                Id: staff_Id,
              },
            },
          ])
        );
      }
      setModalLeaveRequest(false);
    } else {
      toast.error(addStaffLeaveSuccessResponse?.message);
    }
    dispatch(createStaffLeaveRequestSuccess([]));
  }, [addStaffLeaveSuccessResponse]);

  const { updateStaffLeaveSuccessResponse, loadingUpdateStaffLeave } =
    useSelector((state) => ({
      updateStaffLeaveSuccessResponse: state.Staff.updateStaffLeaveSuccess,
    }));

  useEffect(() => {
    if (isEmpty(updateStaffLeaveSuccessResponse)) return;
    if (updateStaffLeaveSuccessResponse?.status === 200) {
      toast.success(updateStaffLeaveSuccessResponse?.message);
      dispatch(getStaffLeaveList());
      if (page === "StaffSingleLeaveRequestPage") {
        dispatch(
          searchStaffLeave([
            {
              staff_data: {
                Id: staff_Id,
              },
            },
          ])
        );
      }
      setModalLeaveRequest(false);
    } else {
      toast.error(updateStaffLeaveSuccessResponse?.message);
    }
    dispatch(updateStaffLeaveSuccess([]));
  }, [updateStaffLeaveSuccessResponse]);

  //Staff Holiday  Data
  const [staffHolidayData, setStaffHolidayData] = useState([]);
  console.log(staffHolidayData);
  const [singleDayUnit, setSingleDayUnit] = useState("");

  useEffect(() => {
    if (isEmpty(staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI))
      return;
    setStaffHolidayData(
      staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI?.find(
        (item) => item?.Id === leaveRequestData?.Staff_Id
      )
    );
    setSingleDayUnit(
      staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI?.find(
        (item) => item?.Id === leaveRequestData?.Staff_Id
      )?.stafftostaffholidaycalendarperiod_list[0]
        ?.StaffHolidayCalendar_QtyOfHoursInSingleDay
    );
  }, [
    staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersAPI,
    leaveRequestData?.Staff_Id,
  ]);

  // Task 24583. Write a day count onto a row, carrying the hours/minutes with
  // it exactly as typing in the Requested Days box does. The hours are left
  // alone until the staff member's hours-per-day has loaded, rather than being
  // zeroed against a unit we don't have yet.
  const withDayCount = (row, days) => {
    const next = { ...row, RequestedDays: String(days) };
    const unit = +singleDayUnit;
    if (unit) {
      const hours = days * unit;
      next.RequestedHours = Math.floor(hours);
      next.RequestedMinutes = ((hours - Math.floor(hours)) * 60).toFixed(0);
    }
    return next;
  };

  // Task 24583. In full-day mode the day count follows the dates. It used to be
  // a number typed by hand next to two date pickers that never touched it, so a
  // request for the 15th to the 18th could sit there saying "1 day" and be
  // approved as one - which is exactly what happened.
  const withFullDayRange = (row, changes) => {
    const next = { ...row, ...changes };
    const days = inclusiveDayCount(next.StartDateTime, next.EndDateTime);
    return days === null ? next : withDayCount(next, days);
  };

  // What the dates say, for the mismatch hint below the Requested Days box.
  // Recalculating on a date change fixes new requests; the hint is how an
  // existing wrong one gets noticed and corrected without having to re-pick a
  // date to trigger it.
  const rangeDayCount = inclusiveDayCount(
    leaveRequestData?.StartDateTime,
    leaveRequestData?.EndDateTime
  );
  const dayCountMismatch =
    leaveRequestData?.IncludeTimeRange === "0" &&
    rangeDayCount !== null &&
    String(leaveRequestData?.RequestedDays ?? "") !== String(rangeDayCount);

  const saving = loadingUpdateStaffLeave || staffLeaveCreateRequestLoading;

  // Task 24584. Tabs, but only when editing: on a brand new request there is no
  // history to show and nothing saved to compare anyone else's leave against.
  // Creating stays the single uninterrupted form it has always been.
  const [activeTab, setActiveTab] = useState("details");
  const showTabs = !!isEdit;
  const currentTab = showTabs ? activeTab : "details";

  const MODAL_TABS = [
    { id: "details", label: "Details", icon: "ri-file-list-2-line" },
    { id: "whoelse", label: "Who else is off", icon: "ri-team-line" },
    { id: "notes", label: "Notes", icon: "ri-sticky-note-line" },
    { id: "history", label: "History", icon: "ri-history-line" },
  ];

  return (
    <>
      <AppModal
        open={modalLeaveRequest}
        onClose={handleCloseModal}
        // Built from parts rather than concatenated, so a missing name gives
        // "Leave Request" rather than "undefined Leave Request" (Task 24484).
        title={[displayName, isEdit ? "Update Leave Request" : "Leave Request"]
          .filter(Boolean)
          .join(" ")}
        icon={<BeachAccessOutlinedIcon />}
        headerAccent="indigo"
        size="md"
        loading={saving}
        loadingText="Saving…"
        submitLabel={isEdit ? "Update Leave Request" : "Submit Leave Request"}
        submitColor="success"
        // Task 24584. Save belongs to the form. On the read-only tabs there is
        // nothing to save, and a Save button under a history list invites an
        // accidental update (and its notification) of a request you only came
        // to look at. AppModal drops to a lone Cancel when onSubmit is absent.
        onSubmit={
          currentTab === "details"
            ? () =>
                isEdit
                  ? handleUpdateLeaveRequest()
                  : handleSubmitLeaveRequest()
            : undefined
        }
        dialogProps={{ sx: { zIndex: resolvedZIndex } }}
      >
        {showTabs && (
          <Nav tabs className="nav-tabs-custom nav-primary mb-3">
            {MODAL_TABS.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames({ active: currentTab === tab.id })}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`${tab.icon} align-middle me-1`} />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        )}

        {currentTab === "whoelse" && (
          <LeaveWhoElseIsOff leave={leaveRequestData} />
        )}

        {currentTab === "notes" && (
          <LeaveRequestNotes leaveId={leaveRequestData?.Id} />
        )}

        {currentTab === "history" && (
          <LeaveRequestHistory leave={leaveRequestData} />
        )}

        <div hidden={currentTab !== "details"}>
          {staffToStaffHolidayCalendarPeriodListOpenMyAndMyTeamMembersLoading ? (
            <LoadingSpinner />
          ) : (
            <Row className="g-3">
              <Col lg={12}>
                <div>
                  <div>
                    <Row>
                      <Col>
                        <h6>Available Holiday Preview</h6>{" "}
                      </Col>
                      <Col lg={12} className={"pb-2"}>
                        {staffHolidayData?.stafftostaffholidaycalendarperiod_list?.map(
                          (periodData) => (
                            <Card className={"p-2 mb-0"} key={periodData.Id}>
                              <div className={"text-muted small"}>
                                {HandleDate(
                                  periodData?.StaffHolidayCalendarPeriod_StartDate
                                )}
                                {" – "}
                                {HandleDate(
                                  periodData?.StaffHolidayCalendarPeriod_EndDate
                                )}
                              </div>
                              <div
                                className={
                                  "d-flex align-items-baseline flex-wrap gap-2 mt-1"
                                }
                              >
                                <span className={"fs-4 fw-semibold"}>
                                  {trimZeros(periodData?.LeaveBalanceDays)}
                                </span>
                                <span className={"fw-semibold"}>
                                  {Number(periodData?.LeaveBalanceDays) === 1
                                    ? "day"
                                    : "days"}{" "}
                                  left
                                </span>
                                {/* Same balance, stated in hours - not a
                                    second, larger allowance. */}
                                <span className={"text-muted small"}>
                                  that is{" "}
                                  {trimZeros(periodData?.LeaveBalanceHours)}{" "}
                                  hours
                                </span>
                              </div>
                            </Card>
                          )
                        )}
                      </Col>
                    </Row>
                  </div>
                  {!isEmpty(myTeamMembersList) && (
                    <div className={""}>
                      <div className={"mb-2"}>
                        <label className={"d-block"}>Whose leave is this?</label>
                        {/* Task 24595. Locked once the request exists. Moving a
                            request onto a different person silently rewrites
                            whose allowance it came off and who the approval
                            chain was built for - and the decisions already
                            recorded against it were made about the original
                            person. Void it and raise a new one instead. */}
                        <div className={"btn-group"} role={"group"}>
                          <button
                            type="button"
                            disabled={isEdit}
                            className={`btn btn-sm ${
                              isForMyself ? "btn-primary" : "btn-outline-primary"
                            }`}
                            onClick={() => {
                              setLeaveRequestData({
                                ...leaveRequestData,
                                Staff_Id: lStaffUserId,
                                StaffLeaveType_Id: "",
                              });
                              setDisplayName(lStaffUserFullName ?? "");
                            }}
                          >
                            Myself
                          </button>
                          <button
                            type="button"
                            disabled={isEdit}
                            className={`btn btn-sm ${
                              isForMyself ? "btn-outline-primary" : "btn-primary"
                            }`}
                            onClick={() => {
                              // Clear the person so the dropdown opens empty
                              // rather than silently keeping "me" selected
                              // under a label that says otherwise.
                              setLeaveRequestData({
                                ...leaveRequestData,
                                Staff_Id: "",
                                StaffLeaveType_Id: "",
                              });
                              setDisplayName("");
                            }}
                          >
                            Someone in my team
                          </button>
                        </div>
                        {isEdit && (
                          <div className="text-muted small mt-1">
                            Can't be changed on an existing request. To move it
                            to someone else, void this one and raise a new one.
                          </div>
                        )}
                      </div>

                      <div hidden={isForMyself}>
                        <label>Select Staff</label>
                        <Select
                          isDisabled={isEdit}
                          className="mb-0 job-select2 mb-2"
                          value={
                            leaveRequestData?.Staff_Id
                              ? {
                                  label: myTeamMembersList?.find(
                                    (item) =>
                                      String(item.Staff_Id) ===
                                      String(leaveRequestData?.Staff_Id)
                                  )?.Staff_FullName,
                                  value: leaveRequestData?.Staff_Id,
                                  id: leaveRequestData?.Staff_Id,
                                }
                              : null
                          }
                          onChange={(e) => {
                            setLeaveRequestData({
                              ...leaveRequestData,
                              Staff_Id: e.id,
                              StaffLeaveType_Id: "", // clear on switch
                            });
                            setDisplayName(e.label);
                          }}
                          options={
                            // "Myself" is the toggle now, so it is no longer
                            // one option among the names.
                            (myTeamMembersList || [])
                              .filter(
                                (item) =>
                                  String(item.Staff_Id) !== String(lStaffUserId)
                              )
                              .map((item) => ({
                                label: item?.Staff_FullName,
                                value: item.Staff_Id,
                                id: item.Staff_Id,
                              }))
                              .sort((a, b) =>
                                String(a.label || "").localeCompare(
                                  String(b.label || ""),
                                  undefined,
                                  { sensitivity: "base", numeric: true }
                                )
                              )
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Col>
              <Col lg={12}>
                <div className={"d-flex align-items-center mt-2"}>
                  <button
                    className={`${
                      leaveRequestData?.IncludeTimeRange === "0"
                        ? "btn btn-primary"
                        : "btn btn-outline-primary"
                    } me-2`}
                    onClick={() => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        IncludeTimeRange: "0",
                        FlagAllDay: "1",
                      });
                    }}
                  >
                    Full Day/s
                  </button>
                  <button
                    className={`${
                      leaveRequestData?.IncludeTimeRange === "1"
                        ? "btn btn-primary"
                        : "btn btn-outline-primary"
                    }`}
                    onClick={() => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        IncludeTimeRange: "1",
                        FlagAllDay: "0",
                      });
                    }}
                  >
                    Partial Day/s
                  </button>
                </div>
              </Col>
              {(hardClash || softClashes.length > 0) && (
                <Col lg={12}>
                  <div
                    className={`alert ${
                      hardClash ? "alert-danger" : "alert-warning"
                    } mb-0`}
                  >
                    <div className={"fw-semibold"}>
                      {hardClash
                        ? "Leave cannot be booked over these dates"
                        : "These dates are restricted"}
                    </div>

                    {(hardClash ? [hardClash] : softClashes).map(
                      (restriction) => (
                        <div key={restriction.Id} className={"small mt-1"}>
                          {HandleDate(restriction.StartDateTime)} to{" "}
                          {HandleDate(restriction.EndDateTime)}
                          {restriction.Notes ? ` - ${restriction.Notes}` : ""}
                        </div>
                      )
                    )}

                    {!hardClash && (
                      <div className={"small mt-2"}>
                        You can still submit this. Whoever approves it will see
                        the same warning.
                      </div>
                    )}
                  </div>
                </Col>
              )}

              <Col lg={12}>
                <div htmlFor="company_name-field" className="form-label">
                  Please select leave duration
                </div>
                <div>
                  <Row>
                    <Col
                      lg={leaveRequestData?.IncludeTimeRange === "1" ? 6 : 12}
                    >
                      {leaveRequestData?.IncludeTimeRange === "0" ? (
                        <Row>
                          <Col lg={6}>
                            <AppDatePicker
                              value={
                                leaveRequestData?.StartDateTime
                                  ? moment(leaveRequestData.StartDateTime)
                                  : null
                              }
                              onChange={(val) => {
                                const jsDate =
                                  val && val.isValid() ? val.toDate() : null;
                                // Task 24583 - the day count follows the dates.
                                setLeaveRequestData((prev) =>
                                  withFullDayRange(prev, {
                                    StartDateTime: jsDate
                                      ? handleDateFormat(jsDate)
                                      : "",
                                  })
                                );
                              }}
                            />
                          </Col>
                          <Col lg={6}>
                            <AppDatePicker
                              value={
                                leaveRequestData?.EndDateTime
                                  ? moment(leaveRequestData.EndDateTime)
                                  : null
                              }
                              onChange={(val) => {
                                const jsDate =
                                  val && val.isValid() ? val.toDate() : null;
                                // Task 24583 - the day count follows the dates.
                                setLeaveRequestData((prev) =>
                                  withFullDayRange(prev, {
                                    EndDateTime: jsDate
                                      ? handleDateFormat(jsDate)
                                      : "",
                                  })
                                );
                              }}
                            />
                          </Col>
                        </Row>
                      ) : (
                        <div>
                          <AppDateTimePicker
                            value={
                              leaveRequestData?.StartDateTime
                                ? moment(leaveRequestData.StartDateTime)
                                : null
                            }
                            onChange={(val) => {
                              const jsDate =
                                val && val.isValid() ? val.toDate() : null;
                              const picked = jsDate;
                              const newStart =
                                timeFormatDateAndTimeNoSeconds(jsDate);
                              setLeaveRequestData((prev) => {
                                const next = {
                                  ...prev,
                                  StartDateTime: newStart,
                                };
                                // Partial days: default the End (To) to at least
                                // the same day as the Start — same day, the user
                                // adjusts the time.
                                const startObj =
                                  picked instanceof Date
                                    ? picked
                                    : new Date(newStart);
                                const prevEnd = prev.EndDateTime
                                  ? new Date(prev.EndDateTime)
                                  : null;
                                const startMidnight = new Date(startObj);
                                startMidnight.setHours(0, 0, 0, 0);
                                const endIsOnOrAfterStart =
                                  prevEnd &&
                                  !isNaN(prevEnd.getTime()) &&
                                  (() => {
                                    const e = new Date(prevEnd);
                                    e.setHours(0, 0, 0, 0);
                                    return (
                                      e.getTime() >= startMidnight.getTime()
                                    );
                                  })();
                                if (!endIsOnOrAfterStart) {
                                  const base =
                                    prevEnd && !isNaN(prevEnd.getTime())
                                      ? prevEnd
                                      : startObj;
                                  const sameDayEnd = new Date(startObj);
                                  sameDayEnd.setHours(
                                    base.getHours(),
                                    base.getMinutes(),
                                    0,
                                    0
                                  );
                                  next.EndDateTime =
                                    timeFormatDateAndTimeNoSeconds(sameDayEnd);
                                }
                                return next;
                              });
                            }}
                          />
                        </div>
                      )}
                    </Col>
                    {leaveRequestData?.IncludeTimeRange === "1" && (
                      <Col lg={6}>
                        <AppDateTimePicker
                          value={
                            leaveRequestData?.EndDateTime
                              ? moment(leaveRequestData.EndDateTime)
                              : null
                          }
                          onChange={(val) => {
                            const jsDate =
                              val && val.isValid() ? val.toDate() : null;
                            setLeaveRequestData({
                              ...leaveRequestData,
                              EndDateTime: timeFormatDateAndTimeNoSeconds(jsDate),
                            });
                          }}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              </Col>

              <Col lg={6}>
                <div className={"mt-2"}>
                  <label>Requested Days</label>
                  <input
                    disabled={leaveRequestData?.IncludeTimeRange === "1"}
                    type={"number"}
                    placeholder={""}
                    className="form-control"
                    value={leaveRequestData?.RequestedDays}
                    onChange={(e) => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        RequestedDays: e.target.value,
                        RequestedHours:
                          //  we want to get hours only from days so we need to floor the value
                          Math.floor(e.target.value * +singleDayUnit),
                        RequestedMinutes: (
                          (e.target.value * +singleDayUnit -
                            Math.floor(e.target.value * +singleDayUnit)) *
                          60
                        ).toFixed(0),
                      });
                    }}
                  />
                  {/* Task 24583. A request for the 15th to the 18th saved as
                      "1 day" and was approved as one. New requests now follow
                      the dates; this is how an already-wrong one gets noticed
                      and corrected without re-picking a date to trigger it. */}
                  {dayCountMismatch && (
                    <div className="text-warning small mt-1">
                      <i className="ri-error-warning-line align-middle me-1" />
                      These dates cover {rangeDayCount}{" "}
                      {rangeDayCount === 1 ? "day" : "days"}.{" "}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={() =>
                          setLeaveRequestData((prev) =>
                            withDayCount(prev, rangeDayCount)
                          )
                        }
                      >
                        Use {rangeDayCount}
                      </button>
                    </div>
                  )}
                </div>
              </Col>
              <Col lg={3}>
                <div className={"mt-2"}>
                  <label>Requested Hours</label>
                  <input
                    disabled={leaveRequestData?.IncludeTimeRange === "0"}
                    type={"number"}
                    placeholder={""}
                    className="form-control"
                    value={leaveRequestData?.RequestedHours}
                    onChange={(e) => {
                      console.log("***************");
                      console.log("+e.target.value", e.target.value);
                      console.log(
                        "1stage math",
                        +e.target.value +
                          +leaveRequestData?.RequestedMinutes / 60
                      );
                      console.log(
                        "+leaveRequestData?.RequestedMinutes",
                        +leaveRequestData?.RequestedMinutes
                      );
                      console.log("+singleDayUnit", +singleDayUnit);
                      console.log("***************");

                      setLeaveRequestData({
                        ...leaveRequestData,
                        RequestedHours: e.target.value,
                        // hours + minutes / single day unit
                        RequestedDays:
                          // Convert Requested hours + Minutes to days
                          (
                            (+e.target.value +
                              +leaveRequestData?.RequestedMinutes / 60) /
                            +singleDayUnit
                          )?.toFixed(2),
                      });
                    }}
                  />
                </div>
              </Col>
              <Col lg={3}>
                <div className={"mt-2"}>
                  <label>Requested Minutes</label>
                  <input
                    disabled={leaveRequestData?.IncludeTimeRange === "0"}
                    type={"number"}
                    placeholder={""}
                    className="form-control"
                    value={leaveRequestData?.RequestedMinutes}
                    onChange={(e) => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        RequestedMinutes: e.target.value,
                        // hours + minutes / single day unit
                        RequestedDays: (
                          (+e.target.value / 60 +
                            +leaveRequestData?.RequestedHours) /
                          +singleDayUnit
                        )?.toFixed(2),
                      });
                    }}
                  />
                </div>
              </Col>
              <Col lg={12}>
                <div className={"mt-2"}>
                  <label>Leave Type</label>
                  <Select
                    className="mb-0 job-select2 "
                    value={{
                      label: staffLeaveTypeListAPI?.find(
                        (item) =>
                          item.Id === leaveRequestData?.StaffLeaveType_Id
                      )?.Name,
                      value: leaveRequestData?.StaffLeaveType_Id,
                      id: leaveRequestData?.StaffLeaveType_Id,
                    }}
                    onChange={(e) => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        StaffLeaveType_Id: e.value,
                      });
                    }}
                    options={
                      !isEmpty(staffLeaveTypeListAPI)
                        ? staffLeaveTypeListData?.map((item) => ({
                            label: item?.Name,
                            value: item.Id,
                            id: item.Id,
                          }))
                        : []
                    }
                    id="country-select"
                  ></Select>
                </div>
              </Col>
              <Col lg={12}>
                <div className={"mt-2"}>
                  <input
                    className={"form-check-input me-2"}
                    type={"checkbox"}
                    checked={
                      leaveRequestData?.FlagStaffEmailNotification === "1"
                    }
                    onChange={(e) => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        FlagStaffEmailNotification: e.target.checked
                          ? "1"
                          : "0",
                      });
                    }}
                  />
                  <label>Email Staff Notification</label>
                </div>
              </Col>

              <Col lg={12}>
                <div className={"mt-2"}>
                  <label>Notes</label>{" "}
                  <textarea
                    className="form-control"
                    id="exampleFormControlTextarea1"
                    rows="3"
                    value={leaveRequestData?.Notes}
                    onChange={(e) => {
                      setLeaveRequestData({
                        ...leaveRequestData,
                        Notes: e.target.value,
                      });
                    }}
                  ></textarea>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </AppModal>
    </>
  );
}
